import { resolveBrandingPreset } from '@/helpers/branding-preset.helper';
import type {
  BrandingPresetConfigurationItem,
  CommunityBranding,
  CommunityBrandingFontConfig,
  CommunityDto,
} from '@/services/types/api.types';

type CommunityBrandingFormValues = NonNullable<CommunityDto['options']['branding']>;

function withFilledDefaults<T extends object>(defaults: T, overrides: Partial<T> | null | undefined): T {
  const merged = { ...defaults } as T;

  (Object.entries(overrides ?? {}) as [keyof T, T[keyof T]][]).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (typeof value === 'string' && value.trim() === '') {
      return;
    }

    merged[key as keyof T] = value as T[keyof T];
  });

  return merged;
}

function mergeFontWithPreset(
  defaultFont: CommunityBrandingFontConfig,
  overrideFont: CommunityBrandingFontConfig | null | undefined,
): CommunityBrandingFontConfig {
  const merged = withFilledDefaults(defaultFont, overrideFont);

  if (merged.source === 'builtin') {
    merged.css_url = null;
  }

  if (merged.source === 'remote-css') {
    merged.builtin_key = null;
  }

  return merged;
}

function buildBrandingFormValues(
  branding: CommunityBranding | null | undefined,
  presets: BrandingPresetConfigurationItem[] | null | undefined,
): CommunityBrandingFormValues {
  const mode = branding?.mode ?? 'default';
  const preset = resolveBrandingPreset(branding, presets);

  if (mode === 'custom') {
    return {
      mode,
      preset_key: preset.key,
      tokens: {
        colors: withFilledDefaults(preset.tokens.colors, branding?.tokens?.colors),
        typography: withFilledDefaults(preset.tokens.typography, branding?.tokens?.typography),
      },
      fonts: {
        heading: mergeFontWithPreset(preset.fonts.heading, branding?.fonts?.heading),
        body: mergeFontWithPreset(preset.fonts.body, branding?.fonts?.body),
      },
    };
  }

  return {
    mode,
    preset_key: preset.key,
    tokens: {
      colors: { ...preset.tokens.colors },
      typography: { ...preset.tokens.typography },
    },
    fonts: {
      heading: { ...preset.fonts.heading },
      body: { ...preset.fonts.body },
    },
  };
}

export { buildBrandingFormValues, mergeFontWithPreset, withFilledDefaults };
